import { Box, Heading, Stack, useDisclosure } from "@chakra-ui/react"
import {
  allFulfillments,
  BlockFulfillmentEntityHydrated,
  EntityId,
  FulfillmentTypes,
} from "@jackfruit/common"
import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useCart } from "~/hooks/useCart"
import { useCurrentPageId } from "~/hooks/useCurrentPageId"
import { useLineItems } from "~/hooks/useLineItems"
import { usePageSession } from "~/hooks/usePageSession"
import { usePreviousActionableBlock } from "~/hooks/usePreviousActionableBlock"
import { actions } from "~/redux/process"
import { scrollTo } from "~/services/Utils"
import { interactionResponse } from "~/services/webvitals/defer"
import CardButtonWrapperV2 from "../fulfillment/CardButtonWrapperV2"
import Wrapper from "../Wrapper"
import FulfillmentUpdateConfirmationDialog from "./FulfillmentUpdateConfirmationDialog"

interface Props {
  config: BlockFulfillmentEntityHydrated
}

export const WHITE = "#FFFFFF"
export const BLACK = "#000000"

const FulfillmentBlockDarkBlack: React.FC<Props> = ({ config }) => {
  const { t } = useTranslation()
  const { mainTitle, fulfillmentStyle } = config
  const pageId = useCurrentPageId()
  const { pageSession } = usePageSession()

  const { previousBlockType, hasMinimumActionableBlockRequirement } =
    usePreviousActionableBlock("fulfillment")

  const defaultElementNameScroll = hasMinimumActionableBlockRequirement
    ? previousBlockType
    : "html"

  const flow = pageSession.pageFlow

  const fulfillmentConfig = useMemo(
    () => ({
      delivery: {
        title: config.deliveryTitle,
        description: config.deliveryDescription,
        icon: config.deliverySvgIcon,
      },
      pickup: {
        title: config.pickupTitle,
        description: config.pickupDescription,
        icon: config.pickupSvgIcon,
      },
    }),
    [
      config.deliveryDescription,
      config.deliverySvgIcon,
      config.deliveryTitle,
      config.pickupDescription,
      config.pickupSvgIcon,
      config.pickupTitle,
    ]
  )

  const { cart } = useCart()
  const { lineItems } = useLineItems(cart.lineItemIds)
  const dispatch = useDispatch()
  const currentFulfillment = cart.fulfillment
  const { isOpen, onOpen: openDialog, onClose: closeDialog } = useDisclosure()
  const [lineItemsToKeep, setLineItemsToKeep] = useState<EntityId[]>([])
  const [lineItemsToRemove, setLineItemsToRemove] = useState<EntityId[]>([])
  const [targetFulfillment, setTargetFulfillment] = useState(
    fulfillmentConfig.delivery
  )

  const onClose = useCallback(() => {
    closeDialog()
    setLineItemsToKeep([])
    setLineItemsToRemove([])
  }, [closeDialog])

  const onOpen = useCallback(
    (data: { lineItemsToKeep: EntityId[]; lineItemsToRemove: EntityId[] }) => {
      setLineItemsToKeep(data.lineItemsToKeep)
      setLineItemsToRemove(data.lineItemsToRemove)
      openDialog()
    },
    [openDialog]
  )

  const onChangeFulfillment = useCallback(
    async (choice: FulfillmentTypes) => {
      if (choice !== currentFulfillment) {
        setTargetFulfillment(fulfillmentConfig[choice])
        await interactionResponse()
        dispatch(
          actions.updateFulfillment({
            pageId,
            onOpen,
            onClose,
            fulfillment: choice,
          })
        )
      }
    },
    [currentFulfillment, dispatch, fulfillmentConfig, onClose, onOpen, pageId]
  )

  const onDialogDecisionConfirm = useCallback(() => {
    dispatch(actions.productCompatibilityDialog({ choice: true }))
    scrollTo(defaultElementNameScroll)
  }, [dispatch, defaultElementNameScroll])

  const onDialogDecisionCancel = useCallback(() => {
    dispatch(actions.productCompatibilityDialog({ choice: false }))
  }, [dispatch])

  // disable the fulfillment selection in these scenarios
  // if at least one picture is still getting uploaded
  // as we are not yet able to decide for the product match
  // only scenario where this does not applies is:
  // store first with no store selected yet
  const someLineItemsAreNotReady = lineItems.some(lineItem => !lineItem.isReady)
  const isStoreFirstAndHasNoStore =
    flow === "store-first" &&
    !Boolean(cart.storeId) &&
    currentFulfillment === "pickup"

  const isDisabled = someLineItemsAreNotReady && !isStoreFirstAndHasNoStore
  const isBoxed = fulfillmentStyle === "Boxed"

  return (
    <Box bg={BLACK} p={isBoxed ? 4 : 0} pb={2}>
      <Wrapper
        color={WHITE}
        bg={BLACK}
        borderRadius="xl"
        maxW="90rem"
        mx="auto"
      >
        <Box textAlign="center" paddingTop={8} paddingBottom={9} mx="auto">
          <Heading
            as="h3"
            fontSize="2xl"
            mb={8}
            color={WHITE}
            textTransform="capitalize"
          >
            {mainTitle}
          </Heading>
          <Stack
            direction="row"
            spacing={{ base: 4, md: 8 }}
            align={{ base: "center", md: "flex-start" }}
            justify="space-between"
          >
            {allFulfillments.map((fulfillmentType, index) => {
              const config = {
                ...fulfillmentConfig[fulfillmentType],
              }

              return (
                <CardButtonWrapperV2
                  key={index}
                  config={config}
                  currentFulfillment={currentFulfillment}
                  fulfillmentType={fulfillmentType}
                  isDisabled={isDisabled}
                  onSetChoice={onChangeFulfillment}
                />
              )
            })}
          </Stack>
        </Box>

        <FulfillmentUpdateConfirmationDialog
          title={t(
            "components.blocks.FulfillmentBlock.UpdateConfirmationDialog.title",
            { title: targetFulfillment.title }
          )}
          description={t(
            "components.blocks.FulfillmentBlock.UpdateConfirmationDialog.description",
            { title: targetFulfillment.title }
          )}
          lineItemsToKeep={lineItemsToKeep}
          lineItemsToRemove={lineItemsToRemove}
          onClose={onDialogDecisionCancel}
          onConfirm={onDialogDecisionConfirm}
          isOpen={isOpen}
        />
      </Wrapper>
    </Box>
  )
}

export default React.memo(FulfillmentBlockDarkBlack)
